import { baseApi, ENDPOINTS } from '@/shared/api';

import type {
    AffiliatePayout,
    CustomPlanRequest,
    CustomPlanRequestPayload,
    PayoutProfile,
    PayoutProfilePayload,
    ReferralAnalytics,
    ReferralCommission,
    ReferralDetail,
    ReferralEnrollResponse,
    ReferralMe,
    ReferredUser,
} from '../model/types/referral';

const referralApi = baseApi
    .enhanceEndpoints({
        addTagTypes: [
            'ReferralMe',
            'ReferralCommissions',
            'ReferralReferrals',
            'ReferralPayoutProfile',
            'ReferralAnalytics',
            'ReferralCustomPlan',
        ],
    })
    .injectEndpoints({
        endpoints: (build) => ({
            getReferralMe: build.query<ReferralMe, void>({
                query: () => ENDPOINTS.affiliate.referralMe,
                providesTags: ['ReferralMe'],
            }),
            enrollReferral: build.mutation<ReferralEnrollResponse, void>({
                query: () => ({
                    url: ENDPOINTS.affiliate.referralEnroll,
                    method: 'POST',
                }),
                invalidatesTags: ['ReferralMe'],
            }),
            getReferralCommissions: build.query<ReferralCommission[], void>({
                query: () => ENDPOINTS.affiliate.referralCommissions,
                providesTags: ['ReferralCommissions'],
            }),
            getReferralReferrals: build.query<ReferredUser[], void>({
                query: () => ENDPOINTS.affiliate.referralReferrals,
                providesTags: ['ReferralReferrals'],
            }),
            getPayoutProfile: build.query<PayoutProfile | null, void>({
                query: () => ENDPOINTS.affiliate.referralPayoutProfile,
                // The backend answers 204 (empty body) when no profile exists yet.
                transformResponse: (response: PayoutProfile | undefined) =>
                    response ?? null,
                providesTags: ['ReferralPayoutProfile'],
            }),
            updatePayoutProfile: build.mutation<
                PayoutProfile,
                PayoutProfilePayload
            >({
                query: (body) => ({
                    url: ENDPOINTS.affiliate.referralPayoutProfile,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['ReferralPayoutProfile', 'ReferralMe'],
            }),
            requestPayout: build.mutation<AffiliatePayout, void>({
                query: () => ({
                    url: ENDPOINTS.affiliate.referralPayoutRequest,
                    method: 'POST',
                }),
                invalidatesTags: ['ReferralMe'],
            }),
            getReferralDetail: build.query<ReferralDetail, number | string>({
                query: (referralId) =>
                    ENDPOINTS.affiliate.referralDetail(referralId),
                providesTags: (_result, _error, referralId) => [
                    { type: 'ReferralReferrals', id: referralId },
                ],
            }),
            getReferralAnalytics: build.query<ReferralAnalytics, void>({
                query: () => ENDPOINTS.affiliate.referralAnalytics,
                providesTags: ['ReferralAnalytics'],
            }),
            getCustomPlanRequests: build.query<CustomPlanRequest[], void>({
                query: () => ENDPOINTS.affiliate.referralCustomPlan,
                providesTags: ['ReferralCustomPlan'],
            }),
            createCustomPlanRequest: build.mutation<
                CustomPlanRequest,
                CustomPlanRequestPayload
            >({
                query: (body) => ({
                    url: ENDPOINTS.affiliate.referralCustomPlan,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['ReferralCustomPlan'],
            }),
        }),
    });

export const {
    useGetReferralMeQuery,
    useEnrollReferralMutation,
    useGetReferralCommissionsQuery,
    useGetReferralReferralsQuery,
    useGetPayoutProfileQuery,
    useUpdatePayoutProfileMutation,
    useRequestPayoutMutation,
    useGetReferralDetailQuery,
    useGetReferralAnalyticsQuery,
    useGetCustomPlanRequestsQuery,
    useCreateCustomPlanRequestMutation,
} = referralApi;
